import {
  Box,
  Button,
  Container,
  Flex,
  SimpleGrid,
  Spacer,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import http from "../../../core/http";
import {
  deleteMyOrder,
  getMySingleReadingOrder,
} from "../../../services/readingOrdersApi";
import { trackEvent } from "../../../shared/analytics";
import Spinner from "../../../shared/components/Spinner";
import ReadingOrderHeader from "../../reading/components/ReadingOrderHeader";
import MyReadingOrderForm from "../components/MyReadingOrderForm";
import { DeleteIcon } from "@chakra-ui/icons";

function MyOrdersEdit() {
  const { readingOrderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [previewReadingOrder, setPreviewReadingOrder] = useState({
    title: "Preview",
    description:
      "Start filling on the form on the right side to see a preview of what your reading order cover will look like.",
  });

  const deleteMyOrderMutation = useMutation(({ readingOrderId }) => {
    return deleteMyOrder(readingOrderId);
  });

  const readingOrderQuery = useQuery(
    ["readingOrder", readingOrderId],
    () => {
      return getMySingleReadingOrder(readingOrderId);
    },
    {
      onSuccess: (data) => {
        setPreviewReadingOrder(data?.data);
      },
    }
  );

  const onClickDelete = () => {
    if (window.confirm("Are you sure you want to delete this reading order?")) {
      deleteMyOrderMutation
        .mutateAsync({ readingOrderId })
        .then(() => {
          trackEvent("my_order_deleted", { reading_order_id: readingOrderId });
          toast({
            title: "Reading order deleted",
            description:
              "Your reading order has been deleted from your orders list.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          navigate("/my-orders");
        })
        .catch(() => {
          toast({
            title: "Error occured",
            description: "Unable to delete reading order.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
    }
  };

  // Handle form submit
  async function handleSubmit(values) {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("description", values.description);
    formData.append("franchise", values.franchise);
    formData.append("type", values.type);
    formData.append("order", values.order);
    formData.append("published", values.published);
    formData.append("from_year", values.fromYear);
    formData.append("to_year", values.toYear);
    formData.append("is_ongoing", values.isOngoing);
    if (values.thumbnail) {
      formData.append("thumbnail", values.thumbnail);
    }
    setLoading(true);
    try {
      http
        .post(`/v1/my-reading-orders/${readingOrderId}/update`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(({ data }) => {
          setLoading(false);
          toast({
            title: "Reading order information updated.",
            description: "You can now edit the reading order.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          navigate(`/my-orders/${data.data.id}`, {
            state: {
              readingOrder: data.data,
            },
          });
        })
        .catch(() => {
          setLoading(false);
        });
      // handle success
    } catch (error) {
      console.error(error);
      setLoading(false);
      // handle error
      toast({
        title: "Error occured",
        description: error.message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }

  if (readingOrderQuery.isLoading) {
    return <Spinner />;
  }

  const readingOrder = readingOrderQuery.data.data;

  return (
    <Container maxW="container.lg">
      <SimpleGrid mt="6" columns={{ sm: 1, md: 1, lg: 2 }} spacing={2}>
        <Box>
          <Box bg="gray.700" borderRadius="lg">
            {/* Header */}
            <ReadingOrderHeader
              readingOrder={{
                readingOrder,
                thumbnail: previewReadingOrder?.thumbnail,
              }}
              progress={0}
              hideProgress
              showFranchise
            />
            {/* UpNext */}
            {previewReadingOrder?.description && (
              <Text p="6">{previewReadingOrder?.description || ""}</Text>
            )}
          </Box>
          <Box p="3" textAlign="center">
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<DeleteIcon />}
              colorScheme="blue"
              textColor={"gray.500"}
              _hover={{ textColor: "red.500" }}
              onClick={onClickDelete}
            >
              Delete this reading order
            </Button>
          </Box>
        </Box>

        {/* Reading order header */}
        <Box bg="gray.700" borderRadius="lg">
          <Box
            py="3"
            px="3"
            borderBottomColor="gray.900"
            borderBottomWidth="1px"
          >
            <Flex justifyContent="center">
              <Text fontSize="sm" color="gray.400" mt="2" ml="1">
                EDIT READING ORDER
              </Text>
              <Spacer />
            </Flex>
          </Box>
          <Box p="6">
            <MyReadingOrderForm
              previewReadingOrder={previewReadingOrder}
              readingOrder={readingOrder}
              setPreviewReadingOrder={setPreviewReadingOrder}
              handleSubmit={handleSubmit}
              loading={loading}
            />
          </Box>
        </Box>
      </SimpleGrid>
    </Container>
  );
}

export default MyOrdersEdit;
